import type { DreamJob, TargetCompany } from '../services/api'

export type WorkStyle = DreamJob['workStyle']
export type Urgency = DreamJob['urgency']
export type CompanyTier = TargetCompany['tier']

export interface DreamJobFormData {
  targetRole: string
  targetIndustry: string
  targetCompanies: TargetCompany[]
  targetJobLinks: string[]
  salaryTarget: string
  salaryMinimum: string
  benefitsPriorities: string[]
  workStyle: WorkStyle
  locationPreferences: string[]
  willingToRelocate: boolean
  priorities: string[]
  dealBreakers: string[]
  targetStartDate: string
  urgency: Urgency
  additionalRequirements: string
}

export const emptyDreamJobForm: DreamJobFormData = {
  targetRole: '',
  targetIndustry: '',
  targetCompanies: [],
  targetJobLinks: [],
  salaryTarget: '',
  salaryMinimum: '',
  benefitsPriorities: [],
  workStyle: 'hybrid',
  locationPreferences: [],
  willingToRelocate: false,
  priorities: [],
  dealBreakers: [],
  targetStartDate: '',
  urgency: 'moderate',
  additionalRequirements: '',
}

export const workStyleLabels: Record<NonNullable<WorkStyle>, string> = {
  remote: 'Remote',
  hybrid: 'Hybrid',
  onsite: 'On-site',
}

export const urgencyLabels: Record<Urgency, string> = {
  urgent: 'Urgent (ASAP)',
  moderate: 'Moderate (1-3 months)',
  flexible: 'Flexible (3+ months)',
}

// Tier is the client's own workbook vocabulary (Goal / Backup / Wild Card) and
// is also used elsewhere (Target Companies tab) to group and rank companies.
// Ordered goal -> backup -> wildcard so a grouped list reads most-wanted first.
export const TIER_ORDER: CompanyTier[] = ['goal', 'backup', 'wildcard']
export const tierGroupLabels: Record<CompanyTier, string> = {
  goal: 'Goal',
  backup: 'Backup',
  wildcard: 'Wild Card',
}

// The server's dream job as the form holds it. A legacy bare-string company
// carries no tier, so it shows as the tier the server derives for it rather
// than a 'goal' this form invented.
export function dreamJobToForm(dreamJob: DreamJob): DreamJobFormData {
  return {
    targetRole: dreamJob.targetRole || '',
    targetIndustry: dreamJob.targetIndustry || '',
    targetCompanies: (dreamJob.targetCompanies || []).map((c: unknown) =>
      typeof c === 'string' ? { name: c, tier: 'wildcard' as const } : (c as TargetCompany)
    ),
    targetJobLinks: dreamJob.targetJobLinks || [],
    salaryTarget: dreamJob.salaryTarget || '',
    salaryMinimum: dreamJob.salaryMinimum || '',
    benefitsPriorities: dreamJob.benefitsPriorities || [],
    workStyle: dreamJob.workStyle || 'hybrid',
    locationPreferences: dreamJob.locationPreferences || [],
    willingToRelocate: dreamJob.willingToRelocate || false,
    priorities: dreamJob.priorities || [],
    dealBreakers: dreamJob.dealBreakers || [],
    targetStartDate: dreamJob.targetStartDate || '',
    urgency: dreamJob.urgency || 'moderate',
    additionalRequirements: dreamJob.additionalRequirements || '',
  }
}

// Saving a dream job replaces the whole document, and the form can sit open
// for minutes. Anything changed elsewhere in that time (the Opportunities
// page's Target Companies tab, another staff member, the client) would be
// silently overwritten by a plain save. So each field the person edited keeps
// their value, and every field they left alone takes the server's current one.
// `base` is the copy the form last agreed with the server on.
export function mergeDreamJob(
  base: DreamJobFormData,
  mine: DreamJobFormData,
  theirs: DreamJobFormData
): { merged: DreamJobFormData; tookRemote: boolean } {
  const merged = { ...mine }
  let tookRemote = false
  for (const key of Object.keys(mine) as (keyof DreamJobFormData)[]) {
    const edited = JSON.stringify(mine[key]) !== JSON.stringify(base[key])
    if (edited) continue
    if (JSON.stringify(theirs[key]) !== JSON.stringify(base[key])) {
      ;(merged as Record<string, unknown>)[key] = theirs[key]
      tookRemote = true
    }
  }
  return { merged, tookRemote }
}

// A date input's value is a calendar day with no time zone. `new Date('2026-10-01')`
// reads it as UTC midnight, which prints as the day before in the Americas.
export function formatCalendarDay(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return value
  const [, y, m, d] = match
  return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}
